import i18n from "i18next";
import { faFileInvoice } from "@fortawesome/pro-regular-svg-icons";
import { AsyncButton } from "src/components/common/buttons/AsyncButton.tsx";
import type { AsyncButtonProps } from "src/components/common/buttons/AsyncButton.tsx";
import { useGenericDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import { ShopOrdersView } from "src/components/views/erp/production/shopOrder/ShopOrdersView.tsx";
import { resolveValueOrProvider, ValueOrProvider } from "src/utils/valueOrProvider.ts";
import { Permissions } from "src/api/generated/iam/permissions.ts";
import { useHasPermission } from "src/components/views/erp/common/userPermissions.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";
import { useCallback } from "react";

export interface OpenShopOrderButtonProps extends Omit<AsyncButtonProps, "onClick"> {
	shopOrderId: ValueOrProvider<number | Promise<number>> | null | undefined;
}

export function useOpenShopOrder() {
	const { openDialog } = useGenericDialog();
	const hasPermission = useHasPermission();
	const openShopOrder = useCallback(
		(shopOrderId: number) => {
			openDialog({
				title: i18n.t("shop_order"),
				agentContext: {
					key: defineAgentUiContextKey("erp.production.shopOrder.dialog"),
					data: { shopOrderId },
					pathSegments: [`${i18n.t("shop_order")} ${shopOrderId}`],
				},
				content: <ShopOrdersView onlyShopOrderId={shopOrderId} />,
			});
		},
		[openDialog],
	);

	return hasPermission(Permissions.Production.ShopOrders.READ) ? openShopOrder : undefined;
}

export const OpenShopOrderButton = ({
	shopOrderId: shopOrderIdProp,
	variant = "menu",
	...props
}: OpenShopOrderButtonProps) => {
	const openShopOrder = useOpenShopOrder();

	if (openShopOrder == null) return null;
	if (shopOrderIdProp == null) return null;

	return (
		<AsyncButton
			label={i18n.t("open_shop_order")}
			icon={faFileInvoice}
			variant={variant}
			onClick={async () => {
				if (shopOrderIdProp == null) return;
				const shopOrderId = await resolveValueOrProvider(shopOrderIdProp);
				openShopOrder(shopOrderId);
			}}
			{...props}
		/>
	);
};
